import React, { useState, useEffect, useRef, useCallback } from 'react';
import './HomePage.css';

function HomePage() {
  const [audioFiles, setAudioFiles] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const audioRef = useRef(null);

  // Load audio files from the audio directory
  useEffect(() => {
    const loadAudioFiles = async () => {
      try {
        // Fetch the manifest file directly from S3
        const response = await fetch('https://monthinbath.s3.eu-west-2.amazonaws.com/audio/manifest.json');
        
        if (!response.ok) {
          throw new Error('manifest.json not found');
        }
        
        const manifestData = await response.json();
        
        const mp3Files = manifestData
          .filter(item => item.file && (item.file.endsWith('.mp3') || item.file.endsWith('.MP3')))
          .sort((a, b) => a.file.localeCompare(b.file)) // Sort alphabetically by filename
          .map((item, index) => ({
            id: index + 1,
            title: item.title || item.file.replace('.mp3', '').replace(/_/g, ' ').replace(/-/g, ' '),
            author: item.author || '',
            file: `https://monthinbath.s3.eu-west-2.amazonaws.com/audio/${item.file}`
          }));

        console.log('Loaded audio files:', mp3Files);
        setAudioFiles(mp3Files);
      } catch (error) {
        console.error('Error loading audio files:', error);
        setAudioFiles([]);
      } finally {
        setLoading(false);
      }
    };

    loadAudioFiles();
  }, []);

  // Define all event handlers before first use
  const handleNext = useCallback(() => {
    if (currentTrack < audioFiles.length - 1) {
      setCurrentTrack(currentTrack + 1);
      setIsPlaying(true);
    }
  }, [currentTrack, audioFiles.length]);

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const restartOrPrevious = useCallback(() => {
    const audio = audioRef.current;
    // If more than 3 seconds into the track, restart it
    // Otherwise, go to previous track
    if (currentTime > 3) {
      audio.currentTime = 0;
      setCurrentTime(0);
    } else if (currentTrack > 0) {
      setCurrentTrack(currentTrack - 1);
      setIsPlaying(true);
    } else {
      // At first track, just restart it
      audio.currentTime = 0;
      setCurrentTime(0);
    }
  }, [currentTime, currentTrack]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      if (currentTrack < audioFiles.length - 1) {
        handleNext();
      } else {
        setIsPlaying(false);
      }
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentTrack, audioFiles.length, handleNext]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      switch(e.key) {
        case ' ':
          e.preventDefault();
          togglePlayPause();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleNext();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          restartOrPrevious();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isPlaying, currentTrack, currentTime, togglePlayPause, handleNext, restartOrPrevious]);

  const handleSeek = (e) => {
    const audio = audioRef.current;
    const value = parseFloat(e.target.value);
    audio.currentTime = value;
    setCurrentTime(value);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (isPlaying && audioRef.current) {
      audioRef.current.play();
    }
  }, [currentTrack, isPlaying]);

  return (
    <div className="home-page" role="application" aria-label="Audio player for The Month in Bath Talking Magazine">
      <header className="home-header">
        <h1 id="app-title">The Month in Bath Talking Magazine</h1>
        <p>Audio recordings player</p>
      </header>

      <main className="home-content">
        {loading && (
          <div className="loading" role="status" aria-live="polite">
            <p>Loading audio files...</p>
          </div>
        )}

        {!loading && audioFiles.length === 0 && (
          <div className="no-audio" role="status">
            <p>No audio files found. Please add MP3 files to the public/audio/ directory.</p>
          </div>
        )}

        {!loading && audioFiles.length > 0 && (
          <div className="audio-player" role="region" aria-labelledby="app-title">
            <div className="now-playing" aria-live="polite" aria-atomic="true">
              <h2 id="current-track-title">
                {audioFiles[currentTrack]?.title || 'No audio loaded'}
              </h2>
              {audioFiles[currentTrack]?.author && (
                <p className="track-author" aria-label={`Author: ${audioFiles[currentTrack].author}`}>
                  {audioFiles[currentTrack].author}
                </p>
              )}
              <p className="track-info" aria-label={`Track ${currentTrack + 1} of ${audioFiles.length}`}>
                Track {currentTrack + 1} of {audioFiles.length}
              </p>
            </div>

          <audio
            ref={audioRef}
            src={audioFiles[currentTrack]?.file}
            preload="metadata"
            aria-labelledby="current-track-title"
          />

          <div className="progress-container">
            <label htmlFor="seek-slider" className="visually-hidden">
              Seek position: {formatTime(currentTime)} of {formatTime(duration)}
            </label>
            <input
              id="seek-slider"
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              className="seek-slider"
              aria-valuemin="0"
              aria-valuemax={duration}
              aria-valuenow={currentTime}
              aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
            />
            <div className="time-display" aria-hidden="true">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          <div className="controls">
            <button
              onClick={restartOrPrevious}
              className="control-btn"
              aria-label={currentTime > 3 ? "Restart current track or go to previous track (Left Arrow)" : currentTrack === 0 ? "Restart track (Left Arrow)" : "Previous track (Left Arrow)"}
            >
              ⏮ Previous
            </button>

            <button
              onClick={togglePlayPause}
              className="control-btn play-pause"
              aria-label={isPlaying ? 'Pause (Spacebar)' : 'Play (Spacebar)'}
            >
              {isPlaying ? '\u23F8 Pause' : '\u23F5 Play'}
            </button>

            <button
              onClick={handleNext}
              disabled={currentTrack === audioFiles.length - 1}
              className="control-btn"
              aria-label="Next track (Right Arrow)"
            >
              Next ⏭
            </button>
          </div>

          <div className="keyboard-shortcuts" role="region" aria-label="Keyboard shortcuts">
            <h3>Keyboard Shortcuts</h3>
            <ul>
              <li><kbd>Space</kbd> - Play/Pause</li>
              <li><kbd>←</kbd> - Restart track or go to previous (if at beginning)</li>
              <li><kbd>→</kbd> - Next track</li>
            </ul>
          </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default HomePage;
